import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useApi } from "../api/context";
import { isApiError } from "../api/errors";
import type { User } from "../api/types";
import { applyBrand } from "../lib/theme";
import { sessionStore, useSessionSnapshot, type Impersonation } from "./session";

type Auth = {
  user: User | null;
  impersonation: Impersonation | null;
  login: (username: string, password: string) => Promise<User>;
  logout: () => void;
  exitImpersonation: () => void;
  markPasswordChanged: () => void;
};
const Ctx = createContext<Auth | null>(null);

export function useAuth(): Auth {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { api } = useApi();
  const qc = useQueryClient();
  const snap = useSessionSnapshot();
  const active = snap.impersonation ?? snap.session;
  const token = active?.token ?? null;

  // Session restore (§17.1 GET /auth/me): refresh the cached user once per token. Offline
  // (NetworkError) keeps the cached user, so a till can keep selling without a connection;
  // a 401 is handled by the client's onUnauthorized.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api.auth
      .me()
      .then((u) => {
        if (!cancelled && sessionStore.token() === token) sessionStore.updateUser(u);
      })
      .catch((e: unknown) => {
        if (isApiError(e) && e.status !== 401) console.warn("Session refresh failed", e.status);
      });
    return () => {
      cancelled = true;
    };
  }, [api, token]);

  // Whoever is signed in changed without this tab's own login (another tab signed in as a
  // different user or tenant): drop everything cached for the previous one, so no screen keeps
  // showing, or writing against, the old tenant's data.
  const identity = active ? `${active.user.tenantId ?? "platform"}:${active.user.userId}` : null;
  const lastIdentity = useRef(identity);
  useEffect(() => {
    if (lastIdentity.current === identity) return;
    const changedUser = lastIdentity.current !== null && identity !== null;
    lastIdentity.current = identity;
    if (changedUser) {
      qc.clear();
      applyBrand(null);
    }
  }, [identity, qc]);

  const login = useCallback(
    async (username: string, password: string) => {
      const res = await api.auth.login(username, password);
      const { token: t, ...user } = res;
      qc.clear();
      sessionStore.setSession({ token: t, user });
      return user;
    },
    [api, qc],
  );

  const exitImpersonation = useCallback(() => {
    const ret = snap.impersonation?.returnUrl ?? null;
    sessionStore.setImpersonation(null);
    qc.clear();
    applyBrand(null);
    if (ret) window.location.assign(ret);
  }, [qc, snap.impersonation]);

  // While impersonating, "Log out" ends the impersonation only (back to pos-admin); it must
  // not also sign out a real tenant session kept on this device.
  const logout = useCallback(() => {
    if (snap.impersonation) return exitImpersonation();
    sessionStore.setSession(null);
    qc.clear();
    applyBrand(null);
  }, [qc, snap.impersonation, exitImpersonation]);

  const markPasswordChanged = useCallback(() => sessionStore.updateUser({ mustChangePassword: false }), []);

  const value = useMemo<Auth>(() => ({ user: active?.user ?? null, impersonation: snap.impersonation, login, logout, exitImpersonation, markPasswordChanged }), [active, snap.impersonation, login, logout, exitImpersonation, markPasswordChanged]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
